import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  SingleSheetPropertyValueType,
  SingleSheetPropertyType,
} from '../model/singleSheetProperty'
import { intl } from '../../../../i18n'
import { WbsItemAdditionalPropertyEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { BasicInformatioTableDef, CellDef } from '../table/model'
import usePrevious from '../../../hooks/usePrevious'
import { DateTermV2 } from '../../../../domain/value-object/DateTermV2'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { MultiLineTextTabDef } from '../tab/model'
import { validateEmail } from './validateEmail'

// TODO: Now only required validation is implemented.
export const useSingleSheetFormValidation = (
  basicInformatioTableDef: BasicInformatioTableDef,
  multiLineTextTabDefs: MultiLineTextTabDef[],
  edited: boolean
): {
  validate: ValidateSingleSheetPropertyValue
  validateAllProperties: ValidateAllSingleSheetPropertyValues
  validationErrors: SingleSheetPropertiesValidationErrors
  hasValidationErrors: boolean
} => {
  const cellDefMap = useMemo(() => {
    const map: CellDefMap = {}
    basicInformatioTableDef.tables.forEach(v => {
      v.rows.forEach(row => {
        row.forEach(cell => {
          map[cell.field] = cell
        })
      })
    })
    return map
  }, [basicInformatioTableDef])
  const tabDefMap = useMemo(() => {
    const map: TabDefMap = {}
    multiLineTextTabDefs.forEach(v => {
      map[v.field] = v
    })
    return map
  }, [multiLineTextTabDefs])
  const [validationErrors, setValidationErrors] =
    useState<SingleSheetPropertiesValidationErrors>(
      {} as SingleSheetPropertiesValidationErrors
    )
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false)

  const validate = useCallback(
    (field: string, value: SingleSheetPropertyValueType) => {
      const cellDef = cellDefMap[field]
      const validationErrors: SingleSheetPropertyValidationError[] = []
      if (cellDef) {
        const requiredValidationError = validateRequiredProperty(cellDef, value)
        if (requiredValidationError) {
          validationErrors.push(requiredValidationError)
        }
        if (SingleSheetPropertyType.MULTI_LINE_TEXT === cellDef.type) {
          const multiLineTextError = validateMultiLineText(value)
          multiLineTextError && validationErrors.push(multiLineTextError)
        } else if ('DATE_TERM' === cellDef.type) {
          const dateTermError = validateDateTerm(cellDef, value)
          dateTermError && validationErrors.push(dateTermError)
        } else if ('EMAIL' === cellDef.type) {
          const emailError = validateEmailProperty(
            value,
            cellDef.emailDomainWhitelist
          )
          emailError && validationErrors.push(emailError)
        }
        const maxLengthError = validateMaxLength(cellDef, value)
        maxLengthError && validationErrors.push(maxLengthError)
      } else {
        const tabDef = tabDefMap[field]
        if (!tabDef) return
        const multiLineTextError = validateMultiLineText(value)
        multiLineTextError && validationErrors.push(multiLineTextError)
      }

      setValidationErrors(prev => {
        return {
          ...prev,
          [field]: validationErrors,
        }
      })
    },
    [cellDefMap, tabDefMap]
  )

  const validateAllProperties = useCallback(
    (formModel: object): ReturnType<ValidateAllSingleSheetPropertyValues> => {
      const allPropertiesValidationErrors: ReturnType<ValidateAllSingleSheetPropertyValues> =
        {}
      Object.entries(cellDefMap).forEach(([field, cellDef]) => {
        if (!cellDef.editable) return
        const value = cellDef.getValue
          ? cellDef.getValue(formModel)
          : formModel[field]
        const validationErrors: SingleSheetPropertyValidationError[] = []
        const requiredValidationError = validateRequiredProperty(cellDef, value)
        if (requiredValidationError) {
          validationErrors.push(requiredValidationError)
        }
        if ('DATE_TERM' === cellDef.type) {
          const dateTermError = validateDateTerm(cellDef, value)
          dateTermError && validationErrors.push(dateTermError)
        } else if ('EMAIL' === cellDef.type) {
          const emailError = validateEmailProperty(
            value,
            cellDef.emailDomainWhitelist
          )
          emailError && validationErrors.push(emailError)
        }
        const maxLengthError = validateMaxLength(cellDef, value)
        maxLengthError && validationErrors.push(maxLengthError)

        if (validationErrors.length === 0) return

        allPropertiesValidationErrors[field] = {
          label: cellDef.label,
          validationErrors,
        }
      })
      multiLineTextTabDefs.forEach(multiLineTextTabDef => {
        const value = formModel[multiLineTextTabDef.field]
        if (!value) return

        const validationError = validateMultiLineText(value)
        if (!validationError) return

        allPropertiesValidationErrors[multiLineTextTabDef.field] = {
          label: multiLineTextTabDef.label,
          validationErrors: [validationError],
        }
      })

      setValidationErrors(
        Object.fromEntries(
          Object.entries(allPropertiesValidationErrors).map(
            ([field, validationResult]) => [
              field,
              validationResult.validationErrors,
            ]
          )
        )
      )
      return allPropertiesValidationErrors
    },
    [cellDefMap, multiLineTextTabDefs]
  )

  const prevEdited = usePrevious(edited)
  useEffect(() => {
    if (prevEdited && !edited) {
      setValidationErrors({})
    }
  }, [edited])

  useEffect(() => {
    const hasErrors = Object.values(validationErrors).some(
      v => (v as SingleSheetPropertyValidationErrors).length > 0
    )
    setHasValidationErrors(hasErrors)
  }, [validationErrors])

  return {
    validate,
    validateAllProperties,
    validationErrors,
    hasValidationErrors,
  }
}

// Validators.
const validateRequiredProperty = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError | undefined => {
  if (!cellDef.required) return undefined

  switch (cellDef.type) {
    case SingleSheetPropertyType.TEXT:
    case SingleSheetPropertyType.EMAIL:
    case SingleSheetPropertyType.MULTI_LINE_TEXT:
      if (value === undefined || value === '') {
        return intl.formatMessage(
          { id: 'validator.required' },
          {
            field: cellDef.label,
          }
        )
      }
      return undefined
    case SingleSheetPropertyType.MULTI_SELECT:
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        return intl.formatMessage(
          { id: 'validator.required' },
          {
            field: cellDef.label,
          }
        )
      }
      return undefined
    case SingleSheetPropertyType.NUMBER:
    case SingleSheetPropertyType.DATE:
    case SingleSheetPropertyType.DATE_TIME:
    case SingleSheetPropertyType.CHECKBOX:
    case SingleSheetPropertyType.SELECT:
    case SingleSheetPropertyType.ENTITY_SEARCH:
      if (value === undefined) {
        return intl.formatMessage(
          { id: 'validator.required' },
          {
            field: cellDef.label,
          }
        )
      }
      return undefined
    case 'DATE_TERM': {
      // 必須の期間は開始日・終了日の両方が必要。
      const dateTerm = value as DateTermV2 | undefined
      if (!dateTerm || !dateTerm.startDate || !dateTerm.endDate) {
        return intl.formatMessage(
          { id: 'validator.required' },
          {
            field: cellDef.label,
          }
        )
      }
      return undefined
    }
    case SingleSheetPropertyType.CHECKBOX: // Skip required validation because this value type is boolean.
    default:
      return
  }
}
const validateDateTerm = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
) => {
  if (cellDef.type !== 'DATE_TERM' || !value) return
  const dateTerm = value as DateTermV2 | undefined
  const startDate = dateTerm?.startDate
  const endDate = dateTerm?.endDate
  if (
    startDate &&
    endDate &&
    !dateV2Service.isEqualOrBefore(startDate, endDate)
  ) {
    return intl.formatMessage({ id: 'validator.dateRange.illegalOrder' })
  }

  return
}
// 最大文字数チェック（TEXT / EMAIL など文字列値の cellDef.maxLength を検証）。
const validateMaxLength = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError | undefined => {
  if (cellDef.maxLength === undefined) return undefined
  if (typeof value !== 'string') return undefined
  if (value.length <= cellDef.maxLength) return undefined
  return intl.formatMessage(
    { id: 'validator.character.maxLength' },
    { maxLength: cellDef.maxLength }
  )
}
const validateEmailProperty = (
  value: SingleSheetPropertyValueType,
  emailDomainWhitelist?: string[]
): SingleSheetPropertyValidationError | undefined => {
  if (typeof value !== 'string') return undefined
  const errorType = validateEmail(value, emailDomainWhitelist)
  switch (errorType) {
    case 'CONTAINS_FULL_WIDTH':
      return intl.formatMessage({ id: 'validator.email.fullWidth' })
    case 'INVALID_FORMAT':
      return intl.formatMessage({ id: 'validator.email.invalidFormat' })
    case 'INVALID_DOMAIN':
      // 旧仕様に合わせ、許可ドメイン一覧をメッセージ末尾に付与する。
      return (
        intl.formatMessage({ id: 'validator.invalidEmailDomain' }) +
        (emailDomainWhitelist ?? []).join(', ')
      )
    default:
      return undefined
  }
}
const validateMultiLineText = (value: SingleSheetPropertyValueType) => {
  if (value && typeof value === 'string' && value.length > 10000) {
    return intl.formatMessage(
      { id: 'validator.character.maxLength' },
      { maxLength: 10000 }
    )
  }

  return
}

// Public types.
export type SingleSheetPropertyValidationError = string
export type SingleSheetPropertyValidationErrors =
  SingleSheetPropertyValidationError[]
export type SingleSheetPropertiesValidationErrors = {
  [field: string]: SingleSheetPropertyValidationError[]
}
export type ValidateSingleSheetPropertyValue = (
  field: string,
  value: SingleSheetPropertyValueType
) => void
export type ValidateAllSingleSheetPropertyValues = (formModel: object) => {
  [field: string]: {
    label: string
    validationErrors: SingleSheetPropertyValidationError[]
  }
}
export type ValidateSingleSheetWbsItemAdditionalPropertyValue = (
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  value: SingleSheetPropertyValueType
) => void
export type ValidateSingleSheetEntityExtensionPropertyValue = (
  value: SingleSheetPropertyValueType
) => void

// Private types.
type CellDefMap = {
  [key: string]: CellDef
}
type TabDefMap = {
  [key: string]: MultiLineTextTabDef
}
